import { useEffect, useState } from 'react'
import {
  Button,
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  TextField,
  Autocomplete,
  TableCell,
  TableRow,
  TableBody,
  TableHead,
  Table,
  Paper,
  TableContainer,
  IconButton,
  Tooltip,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { adminTeamsService } from '../../../services/admin/teams'
import { toast } from 'react-toastify'
import { processTemplateService } from '../../../services/processTemplateService'
import { adminUserReplacementService } from '../../../services/admin/userReplacement'
import {
  CancelOutlined,
  UndoOutlined,
  EditOutlined,
  FilterAltOffOutlined,
  FilterAltOutlined,
} from '@mui/icons-material'
import { FORMATS } from '../../../utility/constants'
import dayjs from 'dayjs'

const STATUS_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Deactivated' },
]

const UserReplacement = () => {
  const { user } = useAuthContext()
  const [openDialog, setOpenDialog] = useState(false)
  const [formData, setFormData] = useState({})
  const [errors, setErrors] = useState({})
  const [users, setUsers] = useState([])
  const [processes, setProcesses] = useState([])
  const [replacements, setReplacements] = useState([])
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [checkingActiveTasks, setCheckingActiveTasks] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [originalFormData, setOriginalFormData] = useState(null)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [deactivateRow, setDeactivateRow] = useState(null)
  const [deactivateActiveTasks, setDeactivateActiveTasks] = useState([])
  const [deactivateClosedTasks, setDeactivateClosedTasks] = useState([])
  const [deactivatePreviewLoading, setDeactivatePreviewLoading] = useState(false)
  const [deactivateSubmitLoading, setDeactivateSubmitLoading] = useState(false)
  const [logDialogOpen, setLogDialogOpen] = useState(false)
  const [logs, setLogs] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [showLogFilters, setShowLogFilters] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [previewTasks, setPreviewTasks] = useState([])
  const [selectedTasks, setSelectedTasks] = useState([])
  const [selectedTaskObjects, setSelectedTaskObjects] = useState([])

  // NEW: status dropdown (All / Active / Deactivated) — independent of the column filters
  const [statusFilter, setStatusFilter] = useState('all')

  const [filters, setFilters] = useState({
    from: '',
    to: '',
    process: '',
    startDate: '',
    endDate: '',
  })

  const [logFilters, setLogFilters] = useState({
    search: '',
    fromDate: '',
    toDate: '',
  })

  const handleDialogClose = () => {
    setOpenDialog(false)
    setFormData({})
    setIsEdit(false)
    setEditingId(null)
    setOriginalFormData(null)
    setErrors({})
  }

  const fetchUsers = async () => {
    const res = await adminTeamsService.getUsers(user)
    setUsers(res || [])
  }

  const fetchProcesses = async () => {
    const res = await processTemplateService.getAllProcessTemplates(user)
    setProcesses(res || [])
  }

  const fetchUserReplacements = async () => {
    try {
      const res = await adminUserReplacementService.getUserReplacement(user)
      setReplacements(res || [])
    } catch (err) {
      console.error(err)
      toast.error('Error fetching replacements')
    }
  }

  useEffect(() => {
    if (user) {
      fetchUsers()
      fetchProcesses()
      fetchUserReplacements()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user])

  const handleChange = (value, field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleDeactivateClick = async (row) => {
    try {
      setDeactivateRow(row)
      setDeactivateActiveTasks([])
      setDeactivateClosedTasks([])
      setDeactivatePreviewLoading(true)
      setDeactivateDialogOpen(true)

      const res = await adminUserReplacementService.previewDeactivateUserReplacement(user, row.id)
      setDeactivateActiveTasks(res?.active_tasks || res?.tasks || [])
      setDeactivateClosedTasks(res?.closed_tasks || [])
    } catch (err) {
      console.error(err)
      toast.error('Error fetching deactivation preview')
      setDeactivateDialogOpen(false)
    } finally {
      setDeactivatePreviewLoading(false)
    }
  }

  const getFullName = (u) => (u ? `${u.first_name} ${u.last_name}` : 'Unknown')

  const currentUserName = getFullName(user)

  const handleViewLog = async () => {
    try {
      const res = await adminUserReplacementService.getUserReplacementLogs(user)
      const data = res?.rows || res || []

      setLogs(data.filter((item) => item !== null && item !== undefined))
      setLogDialogOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Error fetching logs')
    }
  }

  const isReplacementActive = (row) => {
    if (row?.status !== undefined && row?.status !== null) {
      return row.status === 'active'
    }

    if (row?.is_active !== undefined && row?.is_active !== null) {
      return row.is_active === true
    }

    return true
  }

  /**
   * FIX:
   *
   * from_user_id / to_user_id are frozen on the backend the
   * moment a replacement is created — deactivating a replacement
   * only flips `is_active`, it never swaps the two columns
   * (see userReplacements controller: "Do NOT swap from_user_id
   * and to_user_id... Only is_active changes").
   *
   * This function used to swap fromId/toId whenever the
   * replacement was inactive, which made the table display the
   * opposite of what was actually stored as soon as a
   * replacement was deactivated. Always show the real columns.
   */
  const getDisplayUserIds = (row) => ({
    fromId: row.from_user_id,
    toId: row.to_user_id,
  })

  // NEW: status-only filter, always applied regardless of showFilters
  const statusFilteredReplacements = replacements.filter((row) => {
    const active = isReplacementActive(row)

    if (statusFilter === 'active') return active
    if (statusFilter === 'inactive') return !active
    return true
  })

  // Column filters now run on top of the status-filtered list
  const filteredReplacements = statusFilteredReplacements.filter((row) => {
    const { fromId, toId } = getDisplayUserIds(row)

    const fromUsers = users.find((u) => u.id === fromId)
    const toUsers = users.find((u) => u.id === toId)
    const processTemplates = processes.find((p) => p.id === row.process_template_id)

    const fromName = fromUsers ? `${fromUsers.first_name} ${fromUsers.last_name}`.toLowerCase() : ''

    const toName = toUsers ? `${toUsers.first_name} ${toUsers.last_name}`.toLowerCase() : ''

    const processName = processTemplates
      ? (processTemplates.process_template_name || '').toLowerCase()
      : ''

    const rowDate = row.created_at ? dayjs(row.created_at) : null

    const isAfterStart =
      !filters.startDate || (rowDate && rowDate.isAfter(dayjs(filters.startDate).startOf('day')))

    const isBeforeEnd =
      !filters.endDate || (rowDate && rowDate.isBefore(dayjs(filters.endDate).endOf('day')))

    return (
      fromName.includes(filters.from) &&
      toName.includes(filters.to) &&
      processName.includes(filters.process) &&
      isAfterStart &&
      isBeforeEnd
    )
  })

  // Rows actually rendered in the table
  const visibleReplacements = showFilters ? filteredReplacements : statusFilteredReplacements

  const handleSave = async () => {
    try {
      const newErrors = {}

      if (!formData?.from_user_id) {
        newErrors.from_user_id = 'Old user is required'
      }

      if (!formData?.to_user_id) {
        newErrors.to_user_id = 'New user is required'
      }

      if (
        formData?.from_user_id?.id &&
        formData?.to_user_id?.id &&
        formData.from_user_id.id === formData.to_user_id.id
      ) {
        newErrors.to_user_id = 'Old and New user cannot be same'
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors)
        toast.error('Please fill required fields')
        return
      }

      if (isEdit) {
        const toChanged = originalFormData?.to_user_id?.id !== formData?.to_user_id?.id

        if (!toChanged) {
          toast.info('No changes to update')
          handleDialogClose()
          return
        }

        await handleEditPreview()
        return
      }

      await checkActiveTasksBeforeConfirm()
    } catch (err) {
      console.error(err)
      toast.error('Error preparing save')
    }
  }

  const checkActiveTasksBeforeConfirm = async () => {
    try {
      setCheckingActiveTasks(true)

      const payload = {
        from_user_id: formData?.from_user_id?.id,
        process_template_id: formData?.process_templates?.id || null,
      }

      const res = await adminUserReplacementService.previewTasks(user, payload)
      const tasks = res.tasks || []

      if (tasks.length === 0) {
        await saveNewReplacementNoTasks()
        return
      }

      setConfirmDialogOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Error checking active tasks')
    } finally {
      setCheckingActiveTasks(false)
    }
  }

  const formatDate = (date, format) => {
    if (!date) return '-'
    return dayjs(date).format(format)
  }

  const saveNewReplacementNoTasks = async () => {
    const payload = {
      from_user_id: formData?.from_user_id?.id || null,
      to_user_id: formData?.to_user_id?.id || null,
      process_template_id: formData?.process_templates?.id || null,
    }

    const fromName = getFullName(formData?.from_user_id)
    const toName = getFullName(formData?.to_user_id)

    const processName = formData?.process_templates?.process_template_name || 'All Processes'

    await adminUserReplacementService.addUserReplacement(user, payload)

    const logMessage =
      `${currentUserName} added replacement: ` +
      `${fromName} → ${toName} for Process: ${processName}`

    await adminUserReplacementService.addUserReplacementLog(user, {
      log: logMessage,
    })

    await fetchUserReplacements()

    toast.success('Saved successfully!')
    setConfirmDialogOpen(false)
    handleDialogClose()
  }

  const saveToUserOnly = async () => {
    const oldToName = getFullName(originalFormData?.to_user_id)
    const newToName = getFullName(formData?.to_user_id)
    const fromName = getFullName(formData?.from_user_id)

    const processName = formData?.process_templates?.process_template_name || 'All Processes'

    await adminUserReplacementService.updateUserReplacement(user, {
      id: editingId,
      to_user_id: formData?.to_user_id?.id,
    })

    const logMessage =
      `${currentUserName} updated replacement: ` +
      `(${fromName} → ${oldToName}) to ` +
      `(${fromName} → ${newToName}) ` +
      `for Process: ${processName}`

    await adminUserReplacementService.addUserReplacementLog(user, {
      log: logMessage,
    })

    await fetchUserReplacements()

    toast.success('Updated successfully!')
    handleDialogClose()
  }

  const handleFinalSave = async (assignActiveTasks) => {
    try {
      const payload = {
        from_user_id: formData?.from_user_id?.id,
        process_template_id: formData?.process_templates?.id || null,
      }

      if (assignActiveTasks) {
        const res = await adminUserReplacementService.previewTasks(user, payload)
        const tasks = res.tasks || []

        if (tasks.length === 0) {
          await saveNewReplacementNoTasks()
          return
        }

        setPreviewTasks(tasks)
        setSelectedTasks(tasks.map((t) => t.id))
        setSelectedTaskObjects(tasks)
        setPreviewModalOpen(true)
        return
      }

      await saveNewReplacementNoTasks()
    } catch (err) {
      console.error(err)
      toast.error('Error saving')
    }
  }

  const handleEditPreview = async () => {
    try {
      const payload = {
        mode: 'edit',
        replacement_id: editingId,
        exclude_user_id: formData?.to_user_id?.id,
      }

      const res = await adminUserReplacementService.previewTasks(user, payload)
      const tasks = res.tasks || []

      if (tasks.length === 0) {
        await saveToUserOnly()
        return
      }

      setPreviewTasks(tasks)
      setSelectedTasks(tasks.map((t) => t.id))
      setSelectedTaskObjects(tasks)
      setPreviewModalOpen(true)
    } catch (err) {
      console.error(err)
      toast.error('Error fetching active tasks')
    }
  }

  const handleEdit = (row) => {
    const { fromId, toId } = getDisplayUserIds(row)

    const fromUser = users.find((u) => u.id === fromId)
    const toUser = users.find((u) => u.id === toId)
    const processTemplate = processes.find((p) => p.id === row.process_template_id)

    const initialData = {
      from_user_id: fromUser || null,
      to_user_id: toUser || null,
      process_templates: processTemplate || null,
    }

    setFormData(initialData)
    setOriginalFormData(initialData)
    setEditingId(row.id)
    setIsEdit(true)
    setOpenDialog(true)
  }

  const handleDeactivate = async (row) => {
    try {
      setDeactivateSubmitLoading(true)

      const { fromId, toId } = getDisplayUserIds(row)

      const fromUser = users.find((u) => u.id === fromId)
      const toUser = users.find((u) => u.id === toId)

      const fromName = getFullName(fromUser)
      const toName = getFullName(toUser)

      const processName =
        processes.find((p) => p.id === row.process_template_id)?.process_template_name ||
        'All Processes'

      const res = await adminUserReplacementService.deactivateUserReplacement(user, row.id)

      const movedCount =
        res?.moved_count ?? res?.active_moved_count ?? res?.moved_tasks?.length ?? 0

      const closedCount =
        res?.closed_count ?? res?.closed_tasks?.length ?? deactivateClosedTasks.length ?? 0

      let logMessage =
        `${currentUserName} deactivated replacement: ` +
        `${fromName} → ${toName} for Process: ${processName}`

      if (movedCount > 0) {
        const taskNames = deactivateActiveTasks
          .map((task) => task.task_name || task.reference_no || '')
          .filter(Boolean)
          .join(', ')

        logMessage += ` and ${movedCount} active task${
          movedCount !== 1 ? 's' : ''
        } moved back from ${toName} → ${fromName}`

        if (taskNames) {
          logMessage += `; Tasks moved back are: ${taskNames}`
        }
      }

      if (closedCount > 0) {
        logMessage += `; ${closedCount} completed/closed task${
          closedCount !== 1 ? 's' : ''
        } remain with ${toName} for later review`
      }

      await adminUserReplacementService.addUserReplacementLog(user, {
        log: logMessage,
      })

      await fetchUserReplacements()

      toast.success(
        movedCount > 0
          ? `Replacement deactivated and ${movedCount} active task${
              movedCount !== 1 ? 's' : ''
            } moved back to ${fromName}`
          : 'Replacement deactivated successfully!',
      )

      setDeactivateDialogOpen(false)
      setDeactivateRow(null)
      setDeactivateActiveTasks([])
      setDeactivateClosedTasks([])
    } catch (err) {
      console.error(err)

      toast.error(err?.response?.data?.error || 'Error deactivating replacement')
    } finally {
      setDeactivateSubmitLoading(false)
    }
  }

  const filteredLogs = logs
    .filter((log) => log !== null && log !== undefined)
    .filter((log) => {
      const searchMatch =
        !logFilters.search || (log.log || '').toLowerCase().includes(logFilters.search)

      const logDate = log.created_at ? dayjs(log.created_at) : null

      const isAfterStart =
        !logFilters.fromDate ||
        (logDate && logDate.isAfter(dayjs(logFilters.fromDate).startOf('day')))

      const isBeforeEnd =
        !logFilters.toDate || (logDate && logDate.isBefore(dayjs(logFilters.toDate).endOf('day')))

      return searchMatch && isAfterStart && isBeforeEnd
    })
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))

  const filterInputStyle = {
    '& .MuiOutlinedInput-root': {
      height: '20px',
      fontSize: '11px',
      backgroundColor: '#fff',
    },
  }

  const dateInputStyle = {
    '& .MuiOutlinedInput-root': {
      height: '20px',
      fontSize: '11px',
      backgroundColor: '#fff',
      marginBottom: '4px',
    },
    '& input': {
      padding: '2px',
    },
  }

  const initialFilters = {
    from: '',
    to: '',
    process: '',
    startDate: '',
    endDate: '',
  }

  const handleToggleFilters = () => {
    setShowFilters((prev) => {
      const next = !prev

      if (!next) {
        setFilters(initialFilters)
      }

      return next
    })
  }

  const handleLogFilterToggle = () => {
    setShowLogFilters((prev) => {
      const next = !prev

      if (!next) {
        setLogFilters({
          search: '',
          fromDate: '',
          toDate: '',
        })
      }

      return next
    })
  }

  const toggleTask = (task) => {
    setSelectedTaskObjects((prev) => {
      const exists = prev.find((t) => t.id === task.id)

      if (exists) {
        return prev.filter((t) => t.id !== task.id)
      }

      return [...prev, task]
    })

    setSelectedTasks((prev) =>
      prev.includes(task.id) ? prev.filter((id) => id !== task.id) : [...prev, task.id],
    )
  }

  const getTaskNamesString = () => {
    if (!selectedTaskObjects || selectedTaskObjects.length === 0) {
      return ''
    }

    return selectedTaskObjects
      .map((t) => t?.reference_no || t?.task_name || '')
      .filter(Boolean)
      .join(', ')
  }

  const finalizeAfterTaskAssignment = async () => {
    const fromName = getFullName(formData?.from_user_id)
    const toName = getFullName(formData?.to_user_id)

    const processName = formData?.process_templates?.process_template_name || 'All Processes'

    const taskNames = getTaskNamesString()

    if (isEdit) {
      const oldToName = getFullName(originalFormData?.to_user_id)

      let logMessage =
        `${currentUserName} updated replacement: ` +
        `(${fromName} → ${oldToName}) to ` +
        `(${fromName} → ${toName}) ` +
        `for Process: ${processName}`

      if (taskNames) {
        logMessage += ` and Tasks moved are: ${taskNames}`
      }

      await adminUserReplacementService.addUserReplacementLog(user, {
        log: logMessage,
      })
    } else {
      let logMessage =
        `${currentUserName} added replacement: ` +
        `${fromName} → ${toName} for Process: ${processName}`

      if (taskNames) {
        logMessage += ` and Tasks moved are: ${taskNames}`
      }

      await adminUserReplacementService.addUserReplacementLog(user, {
        log: logMessage,
      })
    }

    await fetchUserReplacements()

    toast.success(isEdit ? 'Updated successfully!' : 'Saved successfully!')

    handleDialogClose()
  }

  const handleSubmitSelected = async () => {
    try {
      let targetReplacementId = editingId

      if (!isEdit) {
        const insertPayload = {
          from_user_id: formData?.from_user_id?.id || null,
          to_user_id: formData?.to_user_id?.id || null,
          process_template_id: formData?.process_templates?.id || null,
        }

        const insertRes = await adminUserReplacementService.addUserReplacement(user, insertPayload)

        targetReplacementId = insertRes?.replacement?.id

        setEditingId(targetReplacementId)
      }

      await adminUserReplacementService.applyTasks(user, {
        replacement_id: targetReplacementId,
        to_user_id: formData?.to_user_id?.id,
        selected_task_ids: selectedTasks,
      })

      await finalizeAfterTaskAssignment()

      setPreviewModalOpen(false)
      setSelectedTasks([])
      setSelectedTaskObjects([])
    } catch (err) {
      console.error(err)
      toast.error('Error saving')
    }
  }

  return (
    <>
      <Container>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: 20,
            alignItems: 'center',
          }}
        >
          <Typography variant="h5" sx={{ color: '#656d92' }}>
            User Replacement
          </Typography>

          <Box display="flex" gap={1} alignItems="center">
            {/* NEW: Status dropdown */}

            <Tooltip title={showFilters ? 'Clear Filters' : 'Show Filters'}>
              <IconButton size="small" onClick={handleToggleFilters}>
                {showFilters ? <FilterAltOffOutlined /> : <FilterAltOutlined />}
              </IconButton>
            </Tooltip>

            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              size="small"
              onClick={() => {
                handleViewLog()
                setShowLogFilters(false)
              }}
            >
              View Log
            </Button>

            <Button
              variant="contained"
              size="small"
              onClick={() => {
                setFormData({})
                setErrors({})
                setIsEdit(false)
                setEditingId(null)
                setOriginalFormData(null)
                setOpenDialog(true)
              }}
            >
              Add Replacement
            </Button>
          </Box>
        </div>
      </Container>

      {/* MAIN TABLE */}
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 2,
          boxShadow: 3,
          overflowX: 'auto',
          ml: '24px',
          width: '96%',
        }}
      >
        <Table sx={{ minWidth: 900, tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f7f9ff' }}>
              <TableCell sx={{ width: '20px' }}>
                <b>Sl No</b>
              </TableCell>

              <TableCell sx={{ width: '80px' }}>
                <b>From Users</b>
              </TableCell>

              <TableCell sx={{ width: '80px' }}>
                <b>To Users</b>
              </TableCell>

              <TableCell sx={{ width: '80px' }}>
                <b>Process Templates</b>
              </TableCell>

              <TableCell sx={{ width: '90px' }}>
                <b>Created At</b>
              </TableCell>

              {/* NEW: Updated At column */}
              <TableCell sx={{ width: '90px' }}>
                <b>Updated At</b>
              </TableCell>

              <TableCell sx={{ width: '80px' }}>
                <b>Status</b>
              </TableCell>

              <TableCell align="center" sx={{ width: '120px' }}>
                <b>Actions</b>
              </TableCell>
            </TableRow>

            {showFilters && (
              <TableRow
                sx={{
                  backgroundColor: '#fafbff',
                  '& .MuiTableCell-root': {
                    py: 1,
                    px: 1,
                    borderBottom: '1px solid #e0e0e0',
                  },
                }}
              >
                <TableCell />

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.from}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        from: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.to}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        to: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                <TableCell>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search..."
                    value={filters.process}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        process: e.target.value.toLowerCase(),
                      }))
                    }
                    sx={filterInputStyle}
                  />
                </TableCell>

                <TableCell>
                  <Box display="flex" gap={1}>
                    <TextField
                      type="date"
                      size="small"
                      value={filters.startDate}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      sx={dateInputStyle}
                    />

                    <TextField
                      type="date"
                      size="small"
                      value={filters.endDate}
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      sx={dateInputStyle}
                    />
                  </Box>
                </TableCell>

                <TableCell />

                <TableCell />

                <TableCell />
              </TableRow>
            )}
          </TableHead>

          <TableBody>
            {visibleReplacements.length > 0 ? (
              visibleReplacements.map((row, index) => {
                const active = isReplacementActive(row)

                const { fromId, toId } = getDisplayUserIds(row)

                const fromUsers = users.find((u) => u.id === fromId)

                const toUsers = users.find((u) => u.id === toId)

                const processTemplates = processes.find((p) => p.id === row.process_template_id)

                return (
                  <TableRow
                    key={row.id}
                    sx={{
                      opacity: active ? 1 : 0.65,
                      backgroundColor: active ? 'inherit' : '#fafafa',
                    }}
                  >
                    <TableCell>{index + 1}</TableCell>

                    <TableCell>
                      {fromUsers ? `${fromUsers.first_name} ${fromUsers.last_name}` : fromId}
                    </TableCell>

                    <TableCell>
                      {toUsers ? `${toUsers.first_name} ${toUsers.last_name}` : toId}
                    </TableCell>

                    <TableCell>
                      {processTemplates ? processTemplates.process_template_name : '-'}
                    </TableCell>

                    <TableCell>{formatDate(row.created_at, FORMATS.DATETIME)}</TableCell>

                    {/* NEW: Updated At cell */}
                    <TableCell>
                      {formatDate(row.updated_at || row.modified_at, FORMATS.DATETIME)}
                    </TableCell>

                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          color: active ? '#2e7d32' : '#757575',
                          fontWeight: 600,
                        }}
                      >
                        {active ? 'Active' : 'Deactivated'}
                      </Typography>
                    </TableCell>

                    <TableCell align="center">
                      {active && (
                        <>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => handleEdit(row)}>
                              <EditOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Deactivate">
                            <IconButton size="small" onClick={() => handleDeactivateClick(row)}>
                              <UndoOutlined fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  No Data Found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ADD / EDIT DIALOG */}
      <Dialog open={openDialog} onClose={handleDialogClose} fullWidth maxWidth="sm">
        <DialogTitle>{isEdit ? 'Edit Replacement' : 'Add Replacement'}</DialogTitle>

        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* OLD USER */}
            <Tooltip
              title={isEdit ? 'Old User cannot be changed while editing' : ''}
              disableHoverListener={!isEdit}
            >
              <span>
                <Autocomplete
                  options={users}
                  value={formData.from_user_id || null}
                  getOptionLabel={(u) =>
                    `${u.first_name} ${u.last_name} ${u.email ? `(${u.email})` : ''}`
                  }
                  onChange={(e, val) => {
                    handleChange(val, 'from_user_id')
                    setErrors((prev) => ({
                      ...prev,
                      from_user_id: '',
                    }))
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Old User"
                      size="small"
                      error={!!errors.from_user_id}
                      helperText={errors.from_user_id}
                      fullWidth
                      disabled={isEdit}
                    />
                  )}
                />
              </span>
            </Tooltip>

            {/* NEW USER */}
            <Autocomplete
              options={users}
              value={formData.to_user_id || null}
              getOptionLabel={(u) =>
                `${u.first_name} ${u.last_name} ${u.email ? `(${u.email})` : ''}`
              }
              onChange={(e, val) => {
                handleChange(val, 'to_user_id')
                setErrors((prev) => ({
                  ...prev,
                  to_user_id: '',
                }))
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="New User"
                  size="small"
                  error={!!errors.to_user_id}
                  helperText={errors.to_user_id}
                  fullWidth
                />
              )}
            />

            {/* PROCESS TEMPLATE */}
            <Tooltip
              title={isEdit ? 'Process Template cannot be changed while editing' : ''}
              disableHoverListener={!isEdit}
            >
              <span>
                <Autocomplete
                  options={processes}
                  value={formData.process_templates || null}
                  getOptionLabel={(p) => p.process_template_name}
                  onChange={(e, val) => handleChange(val, 'process_templates')}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  disabled={isEdit}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Process Template"
                      size="small"
                      fullWidth
                      disabled={isEdit}
                    />
                  )}
                />
              </span>
            </Tooltip>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={handleDialogClose}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={
              !formData?.from_user_id ||
              !formData?.to_user_id ||
              formData?.from_user_id?.id === formData?.to_user_id?.id ||
              checkingActiveTasks
            }
          >
            {checkingActiveTasks ? 'Checking...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRM ACTIVE TASKS DIALOG - ADD FLOW ONLY */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Confirmation</DialogTitle>

        <DialogContent>
          <Typography>Do you want to assign the current active tasks to the new user?</Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setConfirmDialogOpen(false)
              handleFinalSave(false)
            }}
          >
            No
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              setConfirmDialogOpen(false)
              handleFinalSave(true)
            }}
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>

      {/* DEACTIVATE PREVIEW DIALOG */}
      <Dialog
        open={deactivateDialogOpen}
        onClose={() => {
          if (!deactivateSubmitLoading) {
            setDeactivateDialogOpen(false)
            setDeactivateRow(null)
            setDeactivateActiveTasks([])
            setDeactivateClosedTasks([])
          }
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Deactivate User Replacement</DialogTitle>

        <DialogContent dividers>
          {deactivateRow && (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* REPLACEMENT INFO */}
              <Typography>You are deactivating the replacement:</Typography>

              <Typography>
                <b>
                  {getFullName(users.find((u) => u.id === getDisplayUserIds(deactivateRow).fromId))}
                </b>
                {' → '}
                <b>
                  {getFullName(users.find((u) => u.id === getDisplayUserIds(deactivateRow).toId))}
                </b>
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Process:{' '}
                {processes.find((p) => p.id === deactivateRow.process_template_id)
                  ?.process_template_name || 'All Processes'}
              </Typography>

              {deactivatePreviewLoading ? (
                <Typography>Checking transferred tasks...</Typography>
              ) : (
                <>
                  {/* IMPORTANT WARNING */}
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: '#fff8e1',
                      borderRadius: 1,
                      border: '1px solid #ffe082',
                    }}
                  >
                    <Typography variant="body2">
                      Deactivating this replacement will move all eligible active tasks that were
                      transferred by this replacement back from{' '}
                      <b>
                        {getFullName(
                          users.find((u) => u.id === getDisplayUserIds(deactivateRow).toId),
                        )}
                      </b>{' '}
                      to{' '}
                      <b>
                        {getFullName(
                          users.find((u) => u.id === getDisplayUserIds(deactivateRow).fromId),
                        )}
                      </b>
                      .
                    </Typography>

                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Future/upcoming tasks governed by this replacement will also use the original
                      user after deactivation.
                    </Typography>
                  </Box>

                  {/* ACTIVE TASKS */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#2e7d32',
                        mb: 1,
                      }}
                    >
                      Active Tasks — Will Move Back
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {deactivateActiveTasks.length > 0
                        ? `${deactivateActiveTasks.length} active task${
                            deactivateActiveTasks.length !== 1 ? 's' : ''
                          } will be moved back to the original user.`
                        : 'No active transferred tasks are currently held by the replacement user.'}
                    </Typography>

                    {deactivateActiveTasks.length > 0 && (
                      <Box
                        sx={{
                          maxHeight: 220,
                          overflowY: 'auto',
                          border: '1px solid #c8e6c9',
                          borderRadius: 1,
                          backgroundColor: '#f9fff9',
                        }}
                      >
                        {deactivateActiveTasks.map((task) => (
                          <Box
                            key={task.id}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom: '1px solid #e8f5e9',
                              '&:last-child': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <Typography variant="body2">
                              {task.task_name || task.reference_no || `Task ${task.id}`}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* CLOSED TASKS */}
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{
                        fontWeight: 600,
                        color: '#d32f2f',
                        mb: 1,
                      }}
                    >
                      Closed / Completed Tasks — Not Moved
                    </Typography>

                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {deactivateClosedTasks.length > 0
                        ? `${deactivateClosedTasks.length} task${
                            deactivateClosedTasks.length !== 1 ? 's' : ''
                          } were completed/closed while working under the replacement. These will remain with the current user for now.`
                        : 'No closed/completed transferred tasks found.'}
                    </Typography>

                    {deactivateClosedTasks.length > 0 && (
                      <Box
                        sx={{
                          maxHeight: 180,
                          overflowY: 'auto',
                          border: '1px solid #ffcdd2',
                          borderRadius: 1,
                          backgroundColor: '#fffafa',
                        }}
                      >
                        {deactivateClosedTasks.map((task) => (
                          <Box
                            key={task.id}
                            sx={{
                              px: 1.5,
                              py: 1,
                              borderBottom: '1px solid #ffebee',
                              '&:last-child': {
                                borderBottom: 'none',
                              },
                            }}
                          >
                            <Typography variant="body2">
                              {task.task_name || task.reference_no || `Task ${task.id}`}
                            </Typography>

                            {task.status && (
                              <Typography variant="caption" color="text.secondary">
                                Status: {task.status}
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Box>

                  {/* FINAL WARNING */}
                  <Box
                    sx={{
                      p: 1.5,
                      backgroundColor: '#f5f5f5',
                      borderRadius: 1,
                      border: '1px solid #ddd',
                    }}
                  >
                    <Typography variant="body2">
                      After deactivation, the replacement will remain in the system as{' '}
                      <b>Deactivated</b>. It will no longer control upcoming tasks.
                    </Typography>

                    {deactivateClosedTasks.length > 0 && (
                      <Typography variant="body2" sx={{ mt: 1, color: '#d32f2f' }}>
                        Note: Closed/completed tasks are only shown for review and will not be moved
                        automatically.
                      </Typography>
                    )}
                  </Box>
                </>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => {
              setDeactivateDialogOpen(false)
              setDeactivateRow(null)
              setDeactivateActiveTasks([])
              setDeactivateClosedTasks([])
            }}
            disabled={deactivateSubmitLoading}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            disabled={deactivatePreviewLoading || deactivateSubmitLoading}
            onClick={() => handleDeactivate(deactivateRow)}
          >
            {deactivateSubmitLoading ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* TASK RE-ASSIGNMENT DIALOG - ADD / EDIT */}
      <Dialog
        open={previewModalOpen}
        onClose={() => setPreviewModalOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Confirm Task Re-assignment</DialogTitle>

        <DialogContent dividers>
          <Typography sx={{ mb: 2 }}>
            {selectedTasks.length} active task
            {selectedTasks.length !== 1 ? 's' : ''} found with{' '}
            <b>
              {previewTasks.length > 0
                ? getFullName(users.find((u) => u.id === previewTasks[0].modified_by))
                : 'the affected user'}
            </b>
            .{' Do you want to assign the selected task'}
            {selectedTasks.length !== 1 ? 's' : ''} to <b>{getFullName(formData?.to_user_id)}</b>?
          </Typography>

          <Box
            sx={{
              maxHeight: 250,
              overflowY: 'auto',
              border: '1px solid #eee',
              borderRadius: 1,
              p: 1,
            }}
          >
            {previewTasks.map((task) => {
              const currentHolder = users.find((u) => u.id === task.modified_by)

              return (
                <Box
                  key={task.id}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                    },
                  }}
                >
                  <Box display="flex" alignItems="center">
                    <Checkbox
                      checked={selectedTasks.includes(task.id)}
                      onChange={() => toggleTask(task)}
                    />

                    <Typography variant="body2">
                      {task.task_name}
                      {currentHolder
                        ? ` (currently active with ${getFullName(currentHolder)})`
                        : ''}
                    </Typography>
                  </Box>
                </Box>
              )
            })}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setPreviewModalOpen(false)}>Cancel</Button>

          <Button
            variant="contained"
            onClick={handleSubmitSelected}
            disabled={selectedTasks.length === 0}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* VIEW LOG DIALOG */}
      <Dialog open={logDialogOpen} onClose={() => setLogDialogOpen(false)} fullWidth maxWidth="md">
        <DialogTitle sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            User Replacement Logs
            <Box display="flex" alignItems="center" gap={1}>
              <Tooltip title={showLogFilters ? 'Clear Filters' : 'Show Filters'} arrow>
                <IconButton onClick={handleLogFilterToggle}>
                  {showLogFilters ? <FilterAltOffOutlined /> : <FilterAltOutlined />}
                </IconButton>
              </Tooltip>

              <IconButton onClick={() => setLogDialogOpen(false)}>
                <CancelOutlined />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: 400,
            }}
          >
            <Table stickyHeader sx={{ tableLayout: 'fixed' }}>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f7f9ff' }}>
                  <TableCell sx={{ width: '60px' }}>
                    <b>Sl No</b>
                  </TableCell>

                  <TableCell sx={{ width: '60%' }}>
                    <b>Log</b>
                  </TableCell>

                  <TableCell sx={{ width: '180px' }}>
                    <b>Created At</b>
                  </TableCell>
                </TableRow>

                {showLogFilters && (
                  <TableRow
                    sx={{
                      backgroundColor: '#fafbff',
                      '& .MuiTableCell-root': {
                        py: 1,
                        px: 1,
                      },
                    }}
                  >
                    <TableCell />

                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="Search log..."
                        value={logFilters.search}
                        onChange={(e) =>
                          setLogFilters((prev) => ({
                            ...prev,
                            search: e.target.value.toLowerCase(),
                          }))
                        }
                        sx={filterInputStyle}
                      />
                    </TableCell>

                    <TableCell>
                      <Box display="flex" gap={0.5}>
                        <TextField
                          type="date"
                          size="small"
                          value={logFilters.fromDate}
                          onChange={(e) =>
                            setLogFilters((prev) => ({
                              ...prev,
                              fromDate: e.target.value,
                            }))
                          }
                          sx={dateInputStyle}
                        />

                        <TextField
                          type="date"
                          size="small"
                          value={logFilters.toDate}
                          onChange={(e) =>
                            setLogFilters((prev) => ({
                              ...prev,
                              toDate: e.target.value,
                            }))
                          }
                          sx={dateInputStyle}
                        />
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableHead>

              <TableBody>
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log, index) => (
                    <TableRow key={log.slno || index} hover>
                      <TableCell>{index + 1}</TableCell>

                      <TableCell
                        sx={{
                          wordBreak: 'break-word',
                        }}
                      >
                        {log.log ?? '-'}
                      </TableCell>

                      <TableCell>{formatDate(log.created_at, FORMATS.DATETIME)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      No Logs Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default UserReplacement
